import { useState } from 'react';
import Image from 'next/image';
import { useTranslation } from 'react-i18next';

import { Box, Flex, Heading, Text, useBreakpointValue } from '@chakra-ui/react';

import { ButtonLink } from '~/components/button-link';
import { VideoModal } from '~/components/video-modal';
import { trackEvent } from '~/features/analytics/track-event';
import { PlayIcon, RocketIcon } from '~/features/enterprise/landing-pages/icons';
import { EnterpriseVideoHeroConfig } from '~/features/enterprise/landing-pages/models/enterprise-platform-landing-page-config';
import { EnterpriseLandingAnalyticsLabels } from '~/features/enterprise/landing-pages/utils/get-enterprise-landing-analytics-labels';
import { HeroLayout } from '~/features/experiments/ab_plp_redesign_2025/components/hero-layout';

const HERO_IMAGE_BORDER_COLOR = '#162B3D';

type EnterpriseVideoHeroProps = {
  analyticsLabels: EnterpriseLandingAnalyticsLabels;
  hero: EnterpriseVideoHeroConfig;
  slug: string;
};

export function EnterpriseVideoHero({ analyticsLabels, hero, slug }: EnterpriseVideoHeroProps) {
  const { t } = useTranslation();
  const tPrefix = `enterprise.${slug}.hero`;
  const [activeVideoId, setActiveVideoId] = useState<string | null>(null);
  const heroImageSrc = hero.heroImage.src;

  const objectPositionDesktopImage = useBreakpointValue(
    { base: 'bottom left', lg: 'center right' },
    { fallback: 'lg' }
  );

  function handlePlayVideo() {
    trackEvent({
      name: 'Enterprise Platform CTA Clicked',
      type: 'click',
      label: analyticsLabels.watchOverviewVideo,
    });
    setActiveVideoId(hero.videoId);
  }

  return (
    <>
      <HeroLayout
        backgroundImageOverlayGradient='linear(90deg,rgba(0, 0, 0, 1) 0%, rgba(255, 255, 255, 0) 50%, rgba(0, 0, 0, 1) 100%)'
        bgColor='#000'
        isolation='isolate'
        minHeight={{ md: '674px' }}
        backgroundImageSlot={
          <>
            <Box display={{ base: 'block', md: 'none' }} inset={0} position='absolute'>
              <Image
                alt=''
                quality={100}
                sizes='100vw'
                src={hero.backgroundImage.mobile}
                style={{
                  filter: 'blur(40px)',
                  minWidth: '496px',
                  objectFit: 'cover',
                  objectPosition: 'left',
                }}
                fill
                priority
              />
            </Box>
            <Box bottom='0px' display={{ base: 'none', md: 'block' }} left={0} position='absolute' right={0} top={0}>
              <Image
                alt=''
                quality={100}
                sizes='100vw'
                src={hero.backgroundImage.desktop}
                style={{
                  filter: 'blur(40px)',
                  objectFit: 'cover',
                  objectPosition: objectPositionDesktopImage,
                }}
                fill
                priority
              />
            </Box>
          </>
        }
        contentContainerProps={{
          gap: {
            base: 12,
          },
          padding: {
            base: 4,
          },
        }}
        ctaContainerProps={{
          flexDirection: {
            base: 'column',
            md: 'row',
          },
        }}
        ctaSlot={
          <>
            <ButtonLink
              linkProps={{ href: '/get-started/enterprise' }}
              buttonProps={{
                flex: { base: 1, md: 'unset' },
                leftIcon: <RocketIcon height='20px' width='20px' />,
                onClick: () =>
                  trackEvent({
                    name: 'Enterprise Platform CTA Clicked',
                    type: 'click',
                    label: analyticsLabels.demoCta,
                  }),
              }}
            >
              {t(`enterprise.${slug}.cta.demoSkills`)}
            </ButtonLink>
            <ButtonLink
              linkProps={{ href: '#' }}
              buttonProps={{
                colorScheme: 'white',
                flex: { base: 1, md: 'unset' },
                leftIcon: <PlayIcon height='20px' width='20px' />,
                variant: 'outline',
                onClick: (e) => {
                  e.preventDefault();
                  handlePlayVideo();
                },
              }}
            >
              {t(`enterprise.${slug}.cta.watchOverview`)}
            </ButtonLink>
          </>
        }
        rightContentSlot={
          <Flex
            alignItems='center'
            display={{ base: 'none', lg: 'flex' }}
            flex='1 1 514px'
            justifyContent='center'
            position='relative'
          >
            <Box
              aria-label='Play video'
              bg='grey.800'
              border={`10px solid ${HERO_IMAGE_BORDER_COLOR}`}
              borderRadius='12px'
              cursor='pointer'
              h='514px'
              maxWidth='514px'
              overflow='hidden'
              position='relative'
              role='button'
              tabIndex={0}
              width='100%'
              onClick={handlePlayVideo}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') handlePlayVideo();
              }}
            >
              <Image
                alt={t(`${tPrefix}.heroImageAlt`)}
                quality={100}
                sizes='514px'
                src={heroImageSrc}
                style={{ backgroundColor: HERO_IMAGE_BORDER_COLOR, objectFit: 'cover' }}
                fill
                priority
              />
              <Flex alignItems='center' aria-hidden='true' inset={0} justifyContent='center' position='absolute'>
                <Flex
                  alignItems='center'
                  bg='blue.500'
                  borderRadius='full'
                  color='white'
                  height='105px'
                  justifyContent='center'
                  width='105px'
                >
                  <PlayIcon boxSize='47px' />
                </Flex>
              </Flex>
            </Box>
          </Flex>
        }
        textAndCtaContainerProps={{
          gap: { base: 6, md: '48px' },
          margin: '0 auto',
          maxWidth: {
            base: '358px',
            md: '100%',
            lg: 'unset',
          },
          width: 'unset',
        }}
        textContentSlot={
          <Flex
            flexDirection='column'
            gap={{ base: 4, md: '25px' }}
            margin='0 auto'
            maxWidth={{ base: '358px', md: '649px', lg: '649px' }}
          >
            <Flex
              alignItems='center'
              display={{ base: 'flex', lg: 'none' }}
              mb='-100px'
              position='relative'
              justifyContent={{
                base: 'center',
                md: 'flex-start',
              }}
            >
              <Box
                bg='grey.800'
                border='10px solid #162B3D'
                borderRadius='12px'
                overflow='hidden'
                position='relative'
                width='100%'
                _after={{
                  bgGradient: 'linear(0deg,rgba(0, 0, 0, 1) 0%, rgba(255, 255, 255, 0) 50%)',
                  content: '""',
                  display: 'block',
                  inset: 0,
                  position: 'absolute',
                }}
                h={{
                  base: '324px',
                  md: '404px',
                }}
                maxWidth={{
                  base: '324px',
                  md: '404px',
                }}
              >
                <Image
                  alt={t(`${tPrefix}.heroImageAlt`)}
                  quality={100}
                  sizes='514px'
                  src={heroImageSrc}
                  style={{ objectFit: 'cover' }}
                  fill
                />
              </Box>
            </Flex>
            <Heading
              as='h1'
              color='white'
              fontSize={{ base: '3.25rem', lg: '4.75rem' }}
              fontWeight='500'
              letterSpacing='0.5px'
              lineHeight={{ base: '1.1', lg: '1.04' }}
              position='relative'
            >
              {t(`${tPrefix}.title`)}
            </Heading>
            <Text color='white' fontSize='17px' fontWeight='500' lineHeight='24px' position='relative'>
              {t(`${tPrefix}.description`)}
            </Text>
          </Flex>
        }
      />
      <VideoModal
        title={t(`${tPrefix}.videoModalTitle`) as string}
        videoId={activeVideoId}
        onClose={() => setActiveVideoId(null)}
      />
    </>
  );
}
